import React, { memo, useCallback, useMemo, useState } from 'react';
import { Button, Divider, Dropdown, Tooltip } from '@bynder/design-system';
import {
    IconAdd,
    IconBuildShape,
    IconCircle,
    IconImage,
    IconRectangle,
    IconTextField,
    IconVideoTemplate,
} from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import useElementCreator from 'packages/pages/editor/useElementCreator';
import useCreateElementShortcuts from 'packages/hooks/shortcuts/useCreateElementShortcuts';
import usePresetShortcuts from 'packages/hooks/shortcuts/usePresetShortcut';
import { WithShortcutLabel } from 'packages/common/WithShortcutLabel';
import useComponentOverview from 'packages/common/Components/ComponentOverview';
import PresetModal from '~/common/editor/presets/PresetsModal';
import ContentPickerModal from 'packages/pages/editor/ContentPickerModal';
import { isImageOrVideo } from '~/common/editor/helpers';
import usePresets from '~/common/editor/presets/usePresets';
import { AddElementWrapper } from './AddLayerButton.styled';

const EMPTY_SELECTED_OBJECT = {};

const AddLayerButton = () => {
    const elementCreator = useElementCreator();
    const [showContentSelectorModal, setShowContentSelectorModal] = useState(false);
    const [showPresetModal, setShowPresetModal] = useState(false);
    const { applyPreset } = usePresets();
    const { translate } = useTranslate();

    const { toggleComponentOverview } = useComponentOverview({
        onApplySuccess: () => {},
    });

    const handleToggleComponentOverview = useCallback(() => {
        toggleComponentOverview();
    }, [toggleComponentOverview]);

    const handleAddPreset = useCallback(
        (selectedPreset, isUniqueContentProperties) => {
            // Temporary solution
            const finalSelectedElement = selectedPreset;
            finalSelectedElement.elements[Object.keys(finalSelectedElement.elements)[0]].name = selectedPreset.name;

            applyPreset(finalSelectedElement, isUniqueContentProperties);
            setShowPresetModal(false);
        },
        [applyPreset],
    );

    const handleConfirmSelection = useCallback(
        (sourceOrArray) => {
            if (Array.isArray(sourceOrArray)) {
                sourceOrArray.forEach((source) => {
                    elementCreator.createImageElement(source);
                });
            } else {
                elementCreator.createImageElement(sourceOrArray);
            }

            setShowContentSelectorModal(false);
        },
        [elementCreator.createImageElement],
    );

    const openContentPicker = useCallback(() => {
        setShowContentSelectorModal(true);
    }, []);

    useCreateElementShortcuts(elementCreator, openContentPicker);
    usePresetShortcuts({ toggleModal: handleToggleComponentOverview });

    const contentPickerModalProps = useMemo(
        () => ({
            type: 'image' as const,
            show: showContentSelectorModal,
            selectedObject: EMPTY_SELECTED_OBJECT,
            frameRate: undefined,
            toggleModal: setShowContentSelectorModal,
            confirmSelection: handleConfirmSelection,
            resizableRange: true,
            isEditorPage: true,
            allowMultiSelect: true, // Always true for AddLayerButton (image only)
        }),
        [showContentSelectorModal, isImageOrVideo],
    );

    return (
        <div>
            {showPresetModal && (
                <PresetModal toggleModal={handleToggleComponentOverview} confirmSelection={handleAddPreset} />
            )}
            <Dropdown
                position="bottom-left"
                trigger={({ isOpen, ...triggerProps }) => (
                    <Tooltip content={translate('editor.layers.sidebar.newLayer')}>
                        <AddElementWrapper>
                            <Button
                                variant="clean"
                                title={translate('editor.layers.sidebar.newLayer')}
                                icon={<IconAdd />}
                                {...triggerProps}
                            />
                        </AddElementWrapper>
                    </Tooltip>
                )}
            >
                <Dropdown.Item icon={<IconImage />} onClick={openContentPicker}>
                    <WithShortcutLabel shortcut={['i']}>{translate('editor.layers.sidebar.image')}</WithShortcutLabel>
                </Dropdown.Item>
                <Dropdown.Item icon={<IconTextField />} onClick={elementCreator.createTextElement}>
                    <WithShortcutLabel shortcut={['t']}>{translate('editor.layers.sidebar.text')}</WithShortcutLabel>
                </Dropdown.Item>
                <Dropdown.Item
                    icon={<IconBuildShape />}
                    submenu={
                        <>
                            <Dropdown.Item icon={<IconRectangle />} onClick={elementCreator.createRectangleElement}>
                                <WithShortcutLabel shortcut={['r']}>
                                    {translate('editor.layers.sidebar.rectangle')}
                                </WithShortcutLabel>
                            </Dropdown.Item>
                            <Dropdown.Item icon={<IconCircle />} onClick={elementCreator.createEllipseElement}>
                                <WithShortcutLabel shortcut={['e']}>
                                    {translate('editor.layers.sidebar.ellipse')}
                                </WithShortcutLabel>
                            </Dropdown.Item>
                        </>
                    }
                >
                    {translate('editor.layers.sidebar.shape')}
                </Dropdown.Item>
                <Divider />
                <Dropdown.Item icon={<IconVideoTemplate />} onClick={handleToggleComponentOverview}>
                    <WithShortcutLabel shortcut={['p']}>
                        {translate('editor.layers.sidebar.components')}
                    </WithShortcutLabel>
                </Dropdown.Item>
            </Dropdown>
            {showContentSelectorModal && <ContentPickerModal {...contentPickerModalProps} />}
        </div>
    );
};

export default memo(AddLayerButton);
